import assert from 'assert-ts';
import type {Schemas} from 'domainTypes/dto';
import {transformErrorResponse} from 'store/api';
import {ebsApi} from 'store/ebsApi';
import type {
  DownloadFile,
  ExternalSuggestions,
  FileInfo,
  FullManifestation,
  ManifestationExternalSuggestions,
  ManifestationV4,
  ManifestationsCache,
  WorkV4,
} from './types';
import {
  mapManifestationBaseDto,
  mapManifestationsCacheDto,
  mapManifestationsDto,
  mapToManifestationBaseDto,
  mapWorkBaseDto,
} from './mappers';

/** All eans share the same cache entry, see ManifestationsCache */
const manifestationsCacheKey = 'manifestations';

const emptyManifestations: ManifestationsCache = {
  manifestationsByEan: {},
  loadedEans: [],
};

export const manifestationApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getManifestation: build.query<
      FullManifestation,
      string,
      Schemas.ManifestationsDto
    >({
      query: (id: string) => ({
        url: 'manifestation/v2/id',
        method: 'POST',
        body: [id],
      }),
      transformResponse: response => {
        return mapManifestationsDto(response)?.[0];
      },
    }),
    getManifestationByEan: build.query<
      FullManifestation,
      string,
      Schemas.ManifestationsDto
    >({
      query: (ean: string) => ({
        url: 'manifestation/v2/ean',
        method: 'POST',
        body: [ean],
      }),
      transformResponse: response => {
        return mapManifestationsDto(response)?.[0];
      },
    }),
    /**
     * Eans fetch from here share a common cache entry: ManifestationsCache,
     * so only the missing ones are fetched and merged into the cache.
     */
    getManifestationsByEans: build.query<ManifestationsCache, string[]>({
      keepUnusedDataFor: 30,
      queryFn: async (eans, _, __, fetchWithBQ) => {
        if (eans.length === 0) {
          return {data: emptyManifestations};
        }

        const response = await fetchWithBQ({
          url: 'manifestation/v2/ean',
          method: 'POST',
          body: eans,
        });
        if (response.error) {
          return {error: response.error};
        }

        return {
          data: mapManifestationsCacheDto(
            response.data as Schemas.ManifestationsDto,
            eans,
          ),
        };
      },
      serializeQueryArgs: () => manifestationsCacheKey,
      merge: (cache, loaded) => {
        Object.assign(cache.manifestationsByEan, loaded.manifestationsByEan);
        cache.loadedEans.push(
          ...loaded.loadedEans.filter(ean => !cache.loadedEans.includes(ean)),
        );
      },
      forceRefetch: ({currentArg}) => (currentArg?.length ?? 0) > 0,
    }),
    getManifestationByIsbn: build.query<
      FullManifestation,
      string,
      Schemas.ManifestationsDto
    >({
      query: (isbn: string) => ({
        url: 'manifestation/v2/isbn',
        method: 'POST',
        body: [isbn],
      }),
      transformResponse: response => {
        return mapManifestationsDto(response)?.[0];
      },
    }),

    saveManifestation: build.mutation<
      {
        manifestation: ManifestationV4;
        work: WorkV4;
      },
      ManifestationV4,
      Schemas.ManifestationWithExpressionDto
    >({
      query: manifestation => ({
        url: `manifestation/v2/${manifestation.id}`,
        method: 'PUT',
        body: mapToManifestationBaseDto(manifestation),
      }),
      transformResponse: ({expression, ...manifestation}) => ({
        manifestation: mapManifestationBaseDto(manifestation),
        work: mapWorkBaseDto(
          assert(
            expression?.work,
            'putManifestation: expected expression.work',
            expression,
          ),
        ),
      }),
      invalidatesTags: r => (r ? [{type: 'Work', id: r.work.id}] : []),
      transformErrorResponse: transformErrorResponse,
    }),

    moveManifestationToExistingWork: build.mutation<
      null,
      {manifestationId: string; targetWorkId: string},
      Schemas.MoveManifestationToExistingWorkDto
    >({
      query: ({manifestationId, targetWorkId}) => ({
        url: `manifestation/${manifestationId}/moveToExistingWork`,
        method: 'POST',
        body: {workId: targetWorkId},
      }),
      invalidatesTags: (_, __, {targetWorkId}) =>
        targetWorkId ? [{type: 'Work', id: targetWorkId}] : [],
    }),

    moveManifestationToDuplicateWork: build.mutation<
      string,
      {manifestationId: string; fromWorkId: string},
      Schemas.MoveManifestationToDuplicateWorkResponseDto
    >({
      query: ({manifestationId, fromWorkId}) => ({
        url: `manifestation/${manifestationId}/moveToDuplicateWork`,
        method: 'POST',
        body: {workId: fromWorkId},
      }),
      transformResponse: r => r.workId!,
      invalidatesTags: (_, __, {fromWorkId}) =>
        fromWorkId ? [{type: 'Work', id: fromWorkId}] : [],
    }),

    deleteManifestation: build.mutation<
      void,
      {manifestationId: string; workId: string},
      Schemas.MoveManifestationToDuplicateWorkResponseDto
    >({
      query: ({manifestationId}) => ({
        url: `manifestation/v2/${manifestationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, {workId}) => [{type: 'Work', id: workId}],
    }),

    /**
     * @deprecated
     */
    getManifestationExternalSuggestions: build.query<
      ExternalSuggestions,
      Pick<ManifestationV4, 'id'>,
      Schemas.ExternalSuggestionsDto
    >({
      keepUnusedDataFor: 60,
      query: ({id}) => `manifestation/${id}/externalSuggestions`,
    }),

    getManifestationExternalSuggestionsByIds: build.query<
      ManifestationExternalSuggestions[],
      string[],
      Schemas.ManifestationExternalSuggestionsDto[]
    >({
      keepUnusedDataFor: 600,
      query: manifestationIds => ({
        url: 'manifestation/externalSuggestions',
        method: 'POST',
        body: manifestationIds,
      }),
      transformErrorResponse: transformErrorResponse,
    }),

    /**
     * Triggered when changerequest is revoked, because backend (Meta) needs us to trigger an outbox update.
     * https://bokbasen.jira.com/browse/BB-6488
     */
    triggerManifestationOutbox: build.mutation<
      void,
      Pick<ManifestationV4, 'id'>,
      void
    >({
      query: ({id}) => ({
        url: `manifestation/v2/${id}/outbox`,
        method: 'POST',
      }),
    }),

    getManifestationFileInfo: build.query<
      FileInfo,
      Pick<ManifestationV4, 'ean'>,
      Schemas.FileInfoDto
    >({
      keepUnusedDataFor: 60,
      query: ({ean}) => `manifestation/${ean}/fileinfo`,
    }),

    getManifestationDownloadFileInfo: build.query<
      DownloadFile,
      Pick<ManifestationV4, 'ean'>,
      Schemas.DownloadFileDto
    >({
      query: ({ean}) => `manifestation/${ean}/download`,
    }),
  }),
});

export const {
  useSaveManifestationMutation,
  useLazyGetManifestationQuery,
  useLazyGetManifestationByEanQuery,
  useGetManifestationsByEansQuery,
  useLazyGetManifestationByIsbnQuery,
  useMoveManifestationToExistingWorkMutation,
  useMoveManifestationToDuplicateWorkMutation,
  useDeleteManifestationMutation,
  useGetManifestationExternalSuggestionsByIdsQuery,
  useTriggerManifestationOutboxMutation,
  useGetManifestationFileInfoQuery,
  useLazyGetManifestationDownloadFileInfoQuery,
} = manifestationApi;
